// frontend/src/slices/usersApiSlice.js
import { apiSlice } from './apiSlice';
import { USERS_URL } from '../constants';

export const userApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({

    login: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/auth`,
        method: 'POST',
        body: data,
      }),
    }),

    // ── NEW: second step — submit the emailed OTP code ──────────────────────
    verifyOtp: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/verify-otp`,
        method: 'POST',
        body: data,                      // { email, otp }
      }),
    }),

    verifyPasswordResetOtp: builder.mutation({
  query: (data) => ({
    url: `${USERS_URL}/verify-reset-otp`,
    method: 'POST',
    body: data,   // { email, otp }
  }),
}),

    pReset: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/preset`,
        method: 'POST',
        body: data,
      }),
    }),

    passwordReset: builder.mutation({
  query: (data) => ({
    url: `${USERS_URL}/resetpass`,
    method: 'POST',
    body: data,   // now just { email, password } — no resetToken
  }),
}),

    register: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}`,
        method: 'POST',
        body: data,
      }),
    }),

    logout: builder.mutation({
      query: () => ({
        url: `${USERS_URL}/logout`,
        method: 'POST',
      }),
    }),

    profile: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/profile`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { _id }) => [
        { type: 'User', id: _id },
        'User',
      ],
    }),

    getUserProDetails: builder.query({
      query: (id) => ({ url: `${USERS_URL}/profile/${id}` }),
      providesTags: (result, error, id) => [{ type: 'User', id }],
      keepUnusedDataFor: 5,
    }),

    getUsers: builder.query({
      query: () => ({ url: USERS_URL }),
      providesTags: ['User'],
      keepUnusedDataFor: 5,
    }),

    getUsersManager: builder.query({
      query: (clinicalDirectorId) => ({
        url: `${USERS_URL}/managers/users/${clinicalDirectorId}`,
      }),
      providesTags: ['User'],
      keepUnusedDataFor: 5,
    }),

    getManagers: builder.query({
      query: () => ({ url: `${USERS_URL}/managers` }),
      providesTags: ['User'],
      keepUnusedDataFor: 5,
    }),

    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `${USERS_URL}/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),

    getUserDetails: builder.query({
      query: (id) => ({ url: `${USERS_URL}/${id}` }),
      providesTags: (result, error, id) => [{ type: 'User', id }],
      keepUnusedDataFor: 5,
    }),

    updateUser: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/${data.userId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: 'User', id: userId },
        'User',
      ],
    }),

    utilUpdateUser: builder.mutation({
      query: () => ({
        url: `${USERS_URL}/updateUserIDs`,
        method: 'PUT',
      }),
      invalidatesTags: ['User'],
    }),

    getStaffUsers: builder.query({
      query: (filters = {}) => ({
        url: `${USERS_URL}/staff`,
        params: filters,
      }),
      providesTags: ['User'],
      keepUnusedDataFor: 5,
      transformResponse: (response) =>
        response.filter((user) => !user.isAdmin && !user.isAgency),
    }),

    getUsersByRole: builder.query({
      query: (role) => ({ url: `${USERS_URL}/role/${role}` }),
      providesTags: ['User'],
      keepUnusedDataFor: 5,
    }),

    getAvailableStaff: builder.query({
      query: ({ startDate, endDate, shiftId, categoryOfStaff }) => ({
        url: `${USERS_URL}/available`,
        params: { startDate, endDate, shiftId, categoryOfStaff },
      }),
      providesTags: ['User'],
      keepUnusedDataFor: 5,
    }),

    getStaffWithShifts: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `${USERS_URL}/staff-with-shifts`,
        params: { startDate, endDate },
      }),
      providesTags: ['User'],
      keepUnusedDataFor: 5,
    }),

    searchStaff: builder.query({
      query: (searchCriteria) => ({
        url: `${USERS_URL}/search`,
        params: searchCriteria,
      }),
      providesTags: ['User'],
      keepUnusedDataFor: 5,
    }),

  }),
});

export const {
  useLoginMutation,
  useVerifyOtpMutation,          // ← new
  useVerifyPasswordResetOtpMutation,
  usePResetMutation,
  usePasswordResetMutation,
  useLogoutMutation,
  useRegisterMutation,
  useProfileMutation,
  useGetUserProDetailsQuery,
  useGetUsersQuery,
  useGetManagersQuery,
  useDeleteUserMutation,
  useUpdateUserMutation,
  useGetUserDetailsQuery,
  useGetUsersManagerQuery,
  useUtilUpdateUserMutation,
  useGetStaffUsersQuery,
  useGetUsersByRoleQuery,
  useGetAvailableStaffQuery,
  useGetStaffWithShiftsQuery,
  useSearchStaffQuery,
} = userApiSlice;